/**
 * Removes Craftly add-on lines when their parent kit leaves the cart.
 *
 * Horizon already hides the child row in the DOM — `onLineItemRemove` drops
 * every row whose `data-parent-key` matches the removed row's `data-key`, and
 * `cart-products.liquid` fills that attribute in from the `_parent` line item
 * property. But the server-side call it makes only clears the single parent
 * line, so the add-on survives in the cart and reappears on the next load.
 *
 * This closes that gap: after any cart update, any add-on whose parent is
 * gone is removed via `/cart/update.js`, which accepts several line keys at
 * once.
 *
 * Runs on document, not per-component, so it covers both the cart page and
 * the cart drawer.
 */

const PARENT_PROPERTY = '_parent';

let inFlight = false;

/**
 * Drops add-on lines whose parent kit is no longer present.
 * @returns {Promise<void>}
 */
async function removeOrphanedAddons() {
  // Guard against re-entry: our own update triggers another cart update event.
  if (inFlight) return;

  let cart;

  try {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return;
    cart = await response.json();
  } catch {
    return;
  }

  const items = Array.isArray(cart?.items) ? cart.items : [];
  if (items.length === 0) return;

  // Variant ids still in the cart under their own steam (i.e. not add-ons).
  const presentParents = new Set(
    items.filter((item) => !item.properties?.[PARENT_PROPERTY]).map((item) => String(item.variant_id))
  );

  const orphans = items.filter((item) => {
    const parent = item.properties?.[PARENT_PROPERTY];
    return parent && !presentParents.has(String(parent));
  });

  if (orphans.length === 0) return;

  // `updates` is keyed by line item key, so several lines can go at once.
  const updates = {};
  for (const orphan of orphans) updates[orphan.key] = 0;

  inFlight = true;

  try {
    await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });

    // Re-render whatever is showing the cart so totals and rows agree.
    document.dispatchEvent(new CustomEvent('craftly:addons-removed', { bubbles: true }));
  } catch {
    /* Leaving the orphan in the cart is recoverable; a failed fetch is not
       worth breaking the page over. */
  } finally {
    inFlight = false;
  }
}

// Shopify's standard cart event, dispatched on document by Horizon after any
// line change (verified against @shopify/events: StandardEvents.cartLinesUpdate).
document.addEventListener('shopify:cart:lines-update', () => {
  // Horizon re-renders the cart section after this event; wait for that to
  // settle before re-reading, or we act on a stale cart and our own removal
  // gets overwritten by the in-flight render.
  setTimeout(removeOrphanedAddons, 300);
});

window.__craftlyCascadeLoaded = true;

export { removeOrphanedAddons };
